import { Injectable } from '@angular/core';
import { MARKETPLACE_AUTH_CONFIG } from './marketplace-auth.config';

const STORAGE_VERIFIER        = 'pkce_verifier';
const STORAGE_STATE           = 'pkce_state';
const STORAGE_REDIRECT_URL    = 'redirect_url';
const STORAGE_POST_LOGOUT_URL = 'post_logout_url';

/** Owns the full Delfi PKCE OAuth2 flow — login, logout, PKCE state management. */
@Injectable({ providedIn: 'root' })
export class MarketplaceAuthService {

  login(): void {
    sessionStorage.setItem(STORAGE_REDIRECT_URL, globalThis.location.pathname + globalThis.location.search);
    this.buildLoginUrl()
      .then(url => { globalThis.location.href = url; })
      .catch(err => {
        console.error('[MarketplaceAuth] login() failed:', err);
        sessionStorage.removeItem(STORAGE_REDIRECT_URL);
      });
  }

  consumeRedirectUrl(): string {
    const saved = sessionStorage.getItem(STORAGE_REDIRECT_URL) ?? '/';
    sessionStorage.removeItem(STORAGE_REDIRECT_URL);
    return saved.includes('/callback') ? '/' : saved;
  }

  logout(idToken: string | null, returnUrl: string): void {
    sessionStorage.setItem(STORAGE_POST_LOGOUT_URL, returnUrl);
    this.clearPkceStorage();

    if (idToken) {
      const params = new URLSearchParams({
        id_token_hint:            idToken,
        post_logout_redirect_uri: `${globalThis.location.origin}/logout`,
      });
      globalThis.location.href = `${MARKETPLACE_AUTH_CONFIG.endSessionUrl}?${params.toString()}`;
    } else {
      globalThis.location.href = `${globalThis.location.origin}/logout`;
    }
  }

  consumePostLogoutUrl(): string {
    const saved = sessionStorage.getItem(STORAGE_POST_LOGOUT_URL) ?? '/';
    sessionStorage.removeItem(STORAGE_POST_LOGOUT_URL);
    const isInternalRoute = saved.includes('/logout') || saved.includes('/callback');
    return isInternalRoute ? '/' : saved;
  }

  getStoredVerifier(): string | null {
    return sessionStorage.getItem(STORAGE_VERIFIER);
  }

  getStoredState(): string | null {
    return sessionStorage.getItem(STORAGE_STATE);
  }

  getRedirectUrl(): string | null {
    return sessionStorage.getItem(STORAGE_REDIRECT_URL);
  }

  clearPkceStorage(): void {
    sessionStorage.removeItem(STORAGE_VERIFIER);
    sessionStorage.removeItem(STORAGE_STATE);
    // STORAGE_REDIRECT_URL is intentionally kept here — consumed separately by consumeRedirectUrl()
  }

  private async buildLoginUrl(): Promise<string> {
    const { authUrl, clientId, serviceClientId } = MARKETPLACE_AUTH_CONFIG;

    const verifier   = this.generateVerifier();
    const challenge  = await this.deriveChallenge(verifier);
    const state      = this.generateState();

    sessionStorage.setItem(STORAGE_VERIFIER, verifier);
    sessionStorage.setItem(STORAGE_STATE, state);

    const params = new URLSearchParams({
      response_type:          'code',
      client_id:              clientId,
      redirect_uri:           `${globalThis.location.origin}/callback`,
      scope:                  `openid ${serviceClientId}`,
      state,
      code_challenge:         challenge,
      code_challenge_method:  'S256',
    });

    return `${authUrl}?${params.toString()}`;
  }

  private generateVerifier(): string {
    const buf = new Uint8Array(32);
    crypto.getRandomValues(buf);
    return this.base64url(buf);
  }

  private async deriveChallenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return this.base64url(new Uint8Array(hash));
  }

  private generateState(): string {
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    return this.base64url(buf);
  }

  private base64url(buf: Uint8Array): string {
    return btoa(String.fromCodePoint(...buf))
      .replaceAll('+', '-')
      .replaceAll('/', '_')
      .replaceAll('=', '');
  }
}
